import React, { useEffect, useRef, useState } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import { Camera, Upload, X, Loader2, ScanLine } from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';

/**
 * Bilingual QR scanner overlay used to identify a loyalty card.
 *
 * Behaviour:
 *  - Opens the device camera (rear-facing on mobile when available).
 *  - Also exposes a "📁 رفع صورة" button to decode a QR from a saved photo
 *    (handy when a customer sends their card by WhatsApp).
 *  - On a successful scan it extracts the 6-digit membership number from the
 *    payload (supports raw numbers or any URL containing `?m=NNNNNN` /
 *    `/loyalty/NNNNNN`) and calls `onDetected(membershipNumber, rawText)`.
 *
 * Props:
 *  - isOpen: boolean
 *  - onClose: () => void
 *  - onDetected: (membershipNumber: string, rawText: string) => void
 *  - isArabic?: boolean
 */
const SCAN_ELEMENT_ID = 'loyalty-qr-scan-region';

const extractMembershipNumber = (raw) => {
  if (!raw) return null;
  const text = String(raw).trim();

  // Plain 5-7 digit string → treat as membership number directly
  if (/^\d{5,7}$/.test(text)) return text;

  // URLs like .../loyalty?m=100023 or .../loyalty/100023
  const mParam = text.match(/[?&]m=(\d{5,7})/);
  if (mParam) return mParam[1];

  const pathPart = text.match(/loyalty\/?#?\/?(\d{5,7})/i);
  if (pathPart) return pathPart[1];

  // Generic fallback: first 5-7 digit sequence anywhere
  const generic = text.match(/(\d{5,7})/);
  if (generic) return generic[1];

  return null;
};

const LoyaltyQRScanner = ({ isOpen, onClose, onDetected, isArabic = true }) => {
  const scannerRef = useRef(null);
  const fileInputRef = useRef(null);
  const [starting, setStarting] = useState(false);
  const [cameraActive, setCameraActive] = useState(false);
  const [error, setError] = useState('');

  // Start / stop camera with the modal lifecycle
  useEffect(() => {
    if (!isOpen) return undefined;
    let cancelled = false;
    const startCamera = async () => {
      setError('');
      setStarting(true);
      try {
        const instance = new Html5Qrcode(SCAN_ELEMENT_ID, { verbose: false });
        scannerRef.current = instance;
        await instance.start(
          { facingMode: 'environment' },
          {
            fps: 12,
            qrbox: { width: 240, height: 240 },
            aspectRatio: 1,
          },
          (decodedText) => handleDecoded(decodedText),
          // ignore frame-level scan errors – they're constant when nothing is in view
          () => {}
        );
        if (!cancelled) setCameraActive(true);
      } catch (err) {
        console.error('QR camera failed', err);
        if (!cancelled) {
          setError(
            isArabic
              ? 'تعذّر تشغيل الكاميرا. تأكد من منح إذن الكاميرا أو استخدم زر رفع صورة.'
              : 'Could not start camera. Please grant permission or use the upload option.'
          );
        }
      } finally {
        if (!cancelled) setStarting(false);
      }
    };
    startCamera();
    return () => {
      cancelled = true;
      const inst = scannerRef.current;
      if (inst) {
        // html5-qrcode's stop() may throw SYNCHRONOUSLY when the scanner never
        // entered the running state (e.g., camera permission denied / no webcam).
        // The async .catch() alone is not enough — wrap with try/catch too so a
        // sync throw can't propagate to React's ErrorBoundary and replace the
        // whole loyalty page with the error fallback.
        try {
          const maybePromise = inst.stop();
          if (maybePromise && typeof maybePromise.catch === 'function') {
            maybePromise.catch(() => {}).finally(() => {
              try { inst.clear(); } catch (_) { /* noop */ }
            });
          } else {
            try { inst.clear(); } catch (_) { /* noop */ }
          }
        } catch (_) {
          try { inst.clear(); } catch (__) { /* noop */ }
        }
      }
      scannerRef.current = null;
      setCameraActive(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleDecoded = async (raw) => {
    const num = extractMembershipNumber(raw);
    if (!num) {
      toast.error(isArabic ? 'الرمز غير معروف' : 'Unrecognized code');
      return;
    }
    // Stop the camera before propagating so the next dialog can use it.
    // Wrap with try/catch — stop() can throw synchronously when not running.
    const inst = scannerRef.current;
    if (inst) {
      try {
        const maybe = inst.stop();
        if (maybe && typeof maybe.then === 'function') {
          try { await maybe; } catch (_) { /* noop */ }
        }
      } catch (_) { /* noop */ }
      try { inst.clear(); } catch (_) { /* noop */ }
      scannerRef.current = null;
    }
    onDetected(num, raw);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setStarting(true);
    setError('');
    try {
      // Use a one-off Html5Qrcode instance for file scanning
      const inst = new Html5Qrcode(`${SCAN_ELEMENT_ID}-file`, { verbose: false });
      const decoded = await inst.scanFile(file, false);
      await handleDecoded(decoded);
    } catch (err) {
      console.error('File QR decode failed', err);
      setError(
        isArabic
          ? 'لم نتمكن من قراءة الرمز من هذه الصورة. تأكد من وضوحها.'
          : 'Could not read the QR code from this image. Please try a clearer photo.'
      );
    } finally {
      setStarting(false);
      e.target.value = '';
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="loyalty-qr-scanner-overlay"
      dir={isArabic ? 'rtl' : 'ltr'}
    >
      <div className="bg-card rounded-2xl border border-white/10 w-full max-w-md p-5 relative shadow-2xl">
        <button
          onClick={onClose}
          className="absolute top-3 left-3 text-muted-foreground hover:text-foreground"
          aria-label="close"
          data-testid="qr-scanner-close-btn"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="text-center mb-4">
          <div className="w-14 h-14 mx-auto rounded-2xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center mb-2">
            <ScanLine className="w-7 h-7 text-white" />
          </div>
          <h2 className="text-lg font-bold">
            {isArabic ? 'مسح رمز بطاقة الولاء' : 'Scan loyalty QR code'}
          </h2>
          <p className="text-xs text-muted-foreground mt-1">
            {isArabic
              ? 'وجّه الكاميرا للرمز أو ارفع صورته من الجهاز'
              : 'Point the camera at the QR or upload a saved photo'}
          </p>
        </div>

        {/* Live camera region */}
        <div
          id={SCAN_ELEMENT_ID}
          className="relative w-full bg-black rounded-xl overflow-hidden mb-3"
          style={{ aspectRatio: '1 / 1' }}
        />
        <div id={`${SCAN_ELEMENT_ID}-file`} className="hidden" />

        {(starting || (!cameraActive && !error)) && (
          <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground mb-3">
            <Loader2 className="w-3 h-3 animate-spin" />
            {isArabic ? 'جاري تهيئة الكاميرا...' : 'Starting camera...'}
          </div>
        )}

        {error && (
          <div className="text-xs text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg p-2 mb-3" data-testid="qr-scanner-error">
            {error}
          </div>
        )}

        <div className="flex gap-2">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleFileUpload}
            data-testid="qr-scanner-file-input"
          />
          <Button
            variant="outline"
            className="flex-1 gap-2"
            onClick={() => fileInputRef.current?.click()}
            disabled={starting}
            data-testid="qr-scanner-upload-btn"
          >
            <Upload className="w-4 h-4" />
            {isArabic ? 'رفع صورة' : 'Upload image'}
          </Button>
          <Button
            variant="outline"
            className="flex-1 gap-2"
            onClick={onClose}
            data-testid="qr-scanner-cancel-btn"
          >
            {isArabic ? 'إلغاء' : 'Cancel'}
          </Button>
        </div>

        <div className="flex items-center justify-center gap-1 mt-3 text-[10px] text-muted-foreground">
          <Camera className="w-3 h-3" />
          {isArabic
            ? 'يدعم الرمز رقم العضوية أو رابط البطاقة'
            : 'Supports a raw membership # or a card URL'}
        </div>
      </div>
    </div>
  );
};

export default LoyaltyQRScanner;
